import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { MotionConfig } from 'framer-motion';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from './components/ui/toast';
import { CookieConsent } from './components/CookieConsent';
import { SplashScreen } from './components/SplashScreen';
import { OtaUpdaterOverlay } from './components/OtaUpdaterOverlay';
import { isNative } from './lib/api-base';
import { checkForOtaUpdate, markBundleReady } from './lib/ota';
import { StatusBar } from '@capacitor/status-bar';
import { routeTree } from './routes/__root';
import { enforceHostSplit } from './lib/host-split';
import { BrandLoader } from './components/ui/BrandLoader';
import { initSentry, Sentry } from './lib/sentry';
import i18n from './i18n'; // Initialize i18n
import './index.css';

/**
 * O host certo ANTES de qualquer outra coisa.
 *
 * Roda antes até do Sentry: se a pessoa está no host errado, este processo
 * inteiro vai ser descartado por um redirecionamento, e tudo que rodasse
 * aqui seria trabalho jogado fora — inclusive uma sessão de erro aberta
 * para uma página que ninguém chegou a ver.
 */
enforceHostSplit();

// Init error monitoring as early as possible (no-op without VITE_SENTRY_DSN).
initSentry();

/**
 * Register the service worker — web only.
 *
 * It exists so my360 is installable: on a phone home screen, and on Horizon
 * OS, where an installed PWA is what can reach WebXR (a WebView shell cannot).
 * Inside the native app it would be redundant at best and a second, competing
 * cache layer at worst, since OTA already owns which bundle is live there.
 *
 * The worker itself never caches navigations ahead of the network, so
 * "update by opening the site again" keeps working exactly as before.
 */
if (!isNative() && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // Not fatal: without it the app still runs, it just isn't installable.
    });
  });
}

// CRITICAL: tell capacitor-updater the bundle launched OK ASAP.
// Without this, the plugin reverts to the previous bundle on next
// launch (assumes the new one crashed), causing the "downloads same
// version every time" loop. Fire on module init, before React even
// mounts — the plugin has a ~10s grace window. No-op on web.
markBundleReady();

// Fullscreen — esconde status bar + edge-to-edge no Android. Combina
// com a flag immersive em MainActivity (esconde nav bar também). No
// iOS o overlaysWebView+hide já dá fullscreen visual completo.
if (isNative()) {
  // Marca o root pra CSS zerar safe-area-inset (WebView desenha
  // SOBRE o notch via LAYOUT_IN_DISPLAY_CUTOUT_MODE_ALWAYS, então
  // não há área a "preservar" — manter padding deixaria faixa preta).
  document.documentElement.classList.add('my360-native');
  StatusBar.hide().catch(() => { /* plugin may be missing in dev */ });
  StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
}

/**
 * Boot splash — Capacitor (APK/iOS) ONLY. Web users skip it: their
 * "splash" is the inline boot loader in index.html, which dies the
 * instant React mounts. Showing the React splash on the web added
 * latency without buying anything (the html boot loader already
 * covered the "page is loading" gap).
 */
function AppShell() {
  const [splashDone, setSplashDone] = useState<boolean>(() => !isNative());

  // Kick off OTA update check after the splash finishes (Capacitor
  // only). Non-blocking — if a new bundle is found, the overlay
  // appears at the bottom and the user can keep using the app.
  useEffect(() => {
    if (!splashDone || !isNative()) return;
    // Small delay so the splash exit animation doesn't compete with
    // the manifest fetch + plugin init for main-thread time.
    const t = window.setTimeout(() => {
      checkForOtaUpdate().catch(() => { /* swallow */ });
    }, 500);
    return () => clearTimeout(t);
  }, [splashDone]);

  return (
    <>
      <RouterProvider router={router} />
      <CookieConsent />
      {!splashDone && <SplashScreen onComplete={() => setSplashDone(true)} />}
      {/* OTA progress UI — renders only when there's an update in
          flight or ready to apply. Native-only by virtue of the OTA
          service being a no-op on web. */}
      {splashDone && isNative() && <OtaUpdaterOverlay />}
    </>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  /**
   * Rota chegando sob demanda não pode virar tela em branco.
   *
   * Com as rotas divididas em pedaços, visitar uma delas dispara um download
   * antes de haver o que renderizar. Sem isto o router entrega null nesse
   * intervalo — o mesmo buraco de um quadro que causava a piscada na
   * entrada, agora a cada navegação para uma tela pesada.
   */
  defaultPendingComponent: () => <BrandLoader variant="loading" />,
  /** Sem atraso: o pedaço pode demorar, e meio segundo de nada é pior. */
  defaultPendingMs: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Dispensa o loader inline SÓ depois que o React pintou.
 *
 * Ele vive fora do #root justamente para o React não apagá-lo ao montar.
 * Duas passagens de rAF garantem que o primeiro quadro do app já foi ao
 * ecrã antes de começarmos o fade — se dispensássemos no mesmo tique do
 * render, voltaríamos ao buraco de um quadro que causava a piscada.
 *
 * Some com fade de 200 ms sobre a mesma marca desenhada pelo BrandLoader,
 * então o que se vê é continuidade, não troca.
 */
function dismissBootLoader() {
  const el = document.getElementById('boot-loader');
  if (!el) return;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      el.style.opacity = '0';
      window.setTimeout(() => el.remove(), 240);
    }),
  );
}

function mount() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={<div style={{ padding: 24 }}>{i18n.t('common:errorFallback')}</div>}>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            {/* Respects prefers-reduced-motion globally for every framer-motion
                animation (232+ usages across the app). Without this, the CSS
                media query alone does nothing for JS-driven motion.*/}
            <MotionConfig reducedMotion="user">
              <AppShell />
            </MotionConfig>
          </ToastProvider>
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
  dismissBootLoader();
}

// Gate the first React paint on i18n being ready. i18next's init() resolves
// asynchronously (microtask) even with bundled resources, so mounting eagerly
// makes the first render use fallbackLng (pt-BR) before the detected/persisted
// language applies — a visible flash back to PT right as the boot loader hands
// off to React. The index.html boot loader (already localized) stays on screen
// until i18n is initialized, so there's no perceptible delay.
if (i18n.isInitialized) {
  mount();
} else {
  i18n.on('initialized', mount);
}
